<!-- TODO: Save recently used tags in user settings -->
<template>
  <div
    v-on-clickaway="away" 
    class="tag-selector-container"
  >
    <span 
      v-show="!searchEnabled"
      class="btn btn-default form-control tag-selector-search-toggle"
      @click="enableSearch"
    >
      <span
        v-show="!selectedTag"
        class="text-muted"
      >
        {{ placeholder }}
      </span>
        
      <span v-show="selectedTag">
        {{ selectedTagFormattedName }}
      </span>
      <i class="caret" />
    </span>

    <input 
      v-show="searchEnabled"
      ref="searchInput"
      v-model="searchString"
      autocomplete="off"
      type="search"
      :placeholder="searchPlaceholder"
      class="form-control tag-selector-search"
      tabindex="-1"
      @focus="activeTab = 0"
    >

    <div
      v-show="searchEnabled"
      class="tag-selector-explore-container"
    >
      <Tabs
        v-model="activeTab"
        custom-nav-class="nav-stacked"
        class="tag-selector-tabs-container"
      >
        <Tab
          :title="$t('SEARCH')"
          index="0"
        >
          <div v-if="(!searchFilteredTags || !searchFilteredTags.length) && recentlyUsedTags.length">
            <div
              v-t="'RECENTLY_USED'"
              class="text-muted p-10 p-b-0"
            />
            <div class="tag-selector-recently-used">
              <a
                v-for="tag in recentlyUsedTags"
                :key="tag.tagType + tag.tagName"
                title="formatRecentTag(tag)"
                class="btn btn-primary"
                @click="selectTag(tag)"
              >
                {{ formatRecentTag(tag) }}
              </a>
            </div>
          </div>

          <div
            v-if="(!searchFilteredTags || !searchFilteredTags.length) && !recentlyUsedTags.length"
            v-t="emptyTabPlaceholderText"
            class="p-10 m-t-1 text-muted"
          />

          <TagTree
            :tags="searchFilteredTags"
            :for-search="true"
            @selectTag="selectTag"
          />
        </Tab>
        <Tab
          v-for="(source, index) in tagSources"
          :key="source"
          :index="index + 1"
          :title="camelCase(source)"
        >
          <TagTree
            :tags="tags"
            :tag-source="source"
            @selectTag="selectTag"
          />
        </Tab>
      </Tabs>
    </div>

    <input
      :disabled="searchEnabled"
      type="text"
      class="tag-selector-offscreen tag-selector-search"
      aria-haspopup="true"
      role="button"
    >
  </div>
</template>

<script>
import { uniq, isEqual, sortBy } from 'lodash';
import { mixin as clickaway } from 'vue-clickaway';

import TagTree from './TagTree';
import messages from './locales';
import sharedMessages from '@/core/locales';
import { camelCaseFormat } from '@/core/filters/camelCaseFormat';


export default {
  components: { TagTree },
  mixins: [ clickaway ],
  props: {
    value: {
      type: Object,
      default() { return null; }
    },
    tags: {
      type: Array,
      default() { return []; }
    },
    recentlyUsedTags: {
      type: Array,
      default() { return []; }
    },
    searchPlaceholder: {
      type: String,
      default() {
        return this.$i18n.t('TAG_SELECTOR_SEARCH_PLACEHOLDER');
      }
    },
    placeholder: {
      type: String,
      default() {
        return this.$i18n.t('TAG_SELECTOR_SELECT_PLACEHOLDER');
      }
    },
    emptyTabPlaceholderText: {
      type: String,
      default: 'TAG_SELECTOR_EMPTY_TAB_PLACEHOLDER_TEXT'
    }
  },
  data() {
    return {
      activeTab: 0,
      searchEnabled: false,
      selectedTag: this.value,
      searchString: ''
    };
  },
  computed: {
    tagSources() {
      return uniq(this.tags.map(t => t.tagSource)).sort();
    },
    lowerSearchString() {
      return this.searchString.toLowerCase();
    },
    searchFilteredTags() {
      if (!this.searchString || !this.searchString.length) {
        return [];
      }

      const camelCase = this.camelCase;
      const lowSearchString = this.lowerSearchString;

      return sortBy(this.tags.filter(function (tag) {
        return tag.tagName.toLowerCase().includes(lowSearchString) 
          || tag.tagType.toLowerCase().includes(lowSearchString) 
          || camelCase(tag.tagName).toLowerCase().includes(lowSearchString) 
          || camelCase(tag.tagType).toLowerCase().includes(lowSearchString)
          || tag.displayName?.toLowerCase().includes(lowSearchString);
      }), 'tagSource');
    },
    selectedTagFormattedName() {
      if (this.selectedTag) {
        return this.camelCase(this.selectedTag.tagSource) 
        + ' - ' 
        + this.camelCase(this.selectedTag.tagType) 
        + ' - ' 
        + (this.selectedTag.displayName || this.camelCase(this.selectedTag.tagName));
      }

      return '';
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue, oldValue) {
        if(!isEqual(newValue, oldValue)) {
          this.selectedTag = newValue;
        }
      }
    }
  },
  mounted () {
    // We do this so the ValidationProvider can validate our custom component's v-model
    // the default events when ValidationProvider does validation are `blur` and `input`
    this.$emit('blur');
  },
  methods: {
    selectTag(tag) {
      this.selectedTag = tag;
      this.searchEnabled = false;
      this.$emit('input', tag);
      this.$emit('change', tag);
    },
    formatRecentTag({ tagType, tagName, displayName }) {
      const name = displayName ?  this.camelCase(displayName) : this.camelCase(tagName)
      return `${this.camelCase(tagType)} - ${name}` 
    },
    camelCase: camelCaseFormat,
    away() {
      this.activeTab = 0;
      this.searchEnabled = false;
    },
    enableSearch() {
      this.searchEnabled = true;

      // Wait for the input element to be visible
      this.$nextTick(function focusSearchInput() {
        this.$refs['searchInput'].focus();
      });
    }
  },
  i18n: {
    messages,
    sharedMessages,
    locale: 'en'
  }
};
</script>

<style lang="scss" scoped>
$tag-selector-left-section-width: 13%;
$recently-used-bg-color: $black;

.tag-selector-container {
  position: relative;

  .tag-selector-search-toggle {
    border-bottom: 0;
    position: relative;
    text-align: left;
    padding: $select-box-custom-padding;

    &.btn-default {
      text-transform: capitalize;
      letter-spacing: normal;
      @include custom-button-variant(var(--component-tag-selector__btn--default-font-color), var(--component-tag-selector__btn--default) !important, var(--component-tag-selector__btn--default));
      .modal-content & {
        @include custom-button-variant($white, var(--component-tag-selector-in-modal__search-toggle-btn--default), var(--component-tag-selector-in-modal__search-toggle-btn--default));
      }
    }

    .caret {
      position: absolute;
      right: 10px;
      height: 10px;
      top: 50%;
      margin-top: -2px;
    }
  }

  .tag-selector-offscreen {
    clip: rect(0 0 0 0)!important;
    width: 1px!important;
    height: 1px!important;
    border: 0!important;
    margin: 0!important;
    padding: 0!important;
    position: absolute!important;
    left: 0!important;
    top: 0!important;
  }

  .tag-selector-explore-container {
    background: var(--component-tag-selector__explore-container);
    margin-bottom: 20px;
    position: absolute;
    top: 40px;
    width: 100%;
    z-index: 3;
    box-shadow: 0 6px 12px rgba($absolute-black, .3); // Box-shadow is same as the one used in dropdown-menu in Bootstrap
    .btn-primary {
      min-height: 0;
      text-transform: capitalize !important;
      letter-spacing: normal !important;
    }
    .modal-content & {
      background: var(--component-tag-selector--in-modal);
    }

    .tag-selector-recently-used {
      display: flex;
      flex-wrap: wrap;
      padding: 0 10px 10px 0px;
      max-height: 130px;
      overflow: hidden;

      > a {
        margin-top: 10px;
        margin-left: 10px;
        padding: 1px 5px;
        height: 20px;
        font-size: 1em;
        @include text-overflow();
      }
    }

    .tag-selector-tabs-container {
      display: flex;
      height: 330px;
      overflow-y: auto;
      overscroll-behavior: contain;

      .nav-tabs {
        flex-basis: $tag-selector-left-section-width;
        &.nav-stacked > li {
          margin-bottom: 0;
        }
      }

      .tab-content {
        flex-grow: 1;
        overflow-y: auto;
        margin-top: 0;
      }
      .tag-selector-tree {
        color: var(--component-tag-selector-font-color);
        padding: 12px 5px;

        .tag-selector-tree-type {
          &:not(:last-of-type) {
            margin-bottom: 15px;
          }

          > .tag-type-name {
            cursor: pointer;
            font-size: 1.2em;

            &:before {
              content: '\f105';
              font-family: "FontAwesome";
              width: 40px;
              display: inline-block;
              text-align: center;
            }

            &.active {
              font-weight: 500;
              &:before {
                content: '\f107';
                color: var(--component-tag-selector__tag-type--active-font-color);
              }
            }
          }

          .tag-selector-tree-name {
            margin: 5px 0 0 40px;

            .tag-name {

              &:not(:last-of-type) {
                margin-bottom: 15px;
              }

              > a {
                cursor: pointer;
                display: inline-block;
                font-size: 1.1em;

                &:before {
                  content: '\f02b';
                  font-family: "FontAwesome";
                  margin-right: 5px;
                  font-size: 0.9em;
                }

                &:hover {
                  color: var(--component-tag-selector__tag-name--hover-font-color);
                }
              }
            }
          }
        }
      }
    }
  }
}
</style>